import React from 'react';
import { Input, InputAdornment, IconButton } from '@material-ui/core';
import { Eye, EyeOff } from 'react-feather';

import BaseInputWrapper from '../BaseInputWrapper';
import InputProps from '../InputProps';
import useDefaultInputHook from '../hooks/useDefaultInputHook';
import { generateProperties } from '../helpers';
import { CustomColor } from '../hooks/useTextInputStyles';
import { colors } from '../../../theme';

interface Props extends InputProps<string> {
  customColor?: CustomColor;
}

const DEFAULT_COLORS: CustomColor = {
  font: colors.madison,
  border: colors.silver,
  icon: colors.white,
};

const PasswordField = ({
  inputId,
  inputName,
  inputLabel,
  onChange,
  defaultValue = '',
  inputError = false,
  inputErrorMessage = '',
  isDisabled = false,
  dataSource,
  customColor = DEFAULT_COLORS,
}: Props): JSX.Element => {
  const formInputProps = generateProperties<string>({
    inputName,
    onChange,
    defaultValue,
    inputError,
    inputErrorMessage,
    dataSource,
  });

  const { currentClass } = useDefaultInputHook({
    error: formInputProps.error,
    defaultValue: formInputProps.value,
    customColor,
    loading: false,
  });
  const [visibleContents, toggleVisibleContents] = React.useState(false);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const newValue = event.target.value;
    formInputProps.onChange(newValue);
  };

  return (
    <BaseInputWrapper
      inputId={inputId}
      label={inputLabel}
      error={formInputProps.error}
      errorMessage={formInputProps.errorText}
      disabled={isDisabled}
      colorLabel={customColor.font}
    >
      <Input
        data-testid="password-field"
        id={inputId}
        name={formInputProps.name}
        value={formInputProps.value}
        className={currentClass}
        onChange={handleChange}
        type={visibleContents ? 'text' : 'password'}
        disabled={isDisabled}
        endAdornment={
          <InputAdornment position="end">
            <IconButton
              data-testid="toggle-visibility-button"
              style={{ padding: 0 }}
              onClick={(): void => toggleVisibleContents(!visibleContents)}
            >
              {visibleContents ? (
                <EyeOff fill={customColor.font} color={customColor.icon} />
              ) : (
                <Eye fill={customColor.font} color={customColor.icon} />
              )}
            </IconButton>
          </InputAdornment>
        }
      />
    </BaseInputWrapper>
  );
};

export default PasswordField;
